import { Controller } from "@hotwired/stimulus";
import { Offcanvas, Modal } from "bootstrap";
import { safeParse, normalizeForStorage, persistToLocalStorage, computeCartETag, persistString } from "../utils/cart_utils";

// Page-level controller for Store.
// - Accepts all products via productsValue (Array)
// - Writes `products` to localStorage on connect
// - Handles Add to Cart keyed by Stripe price_id (supports future tiered pricing)
// - Maintains `cart` and `cart_etag` in localStorage
export default class extends Controller {
  static targets = [
    "qty",
    "priceOption",
    "addButton",
    "bulkOrderModal",
    "bulkOrderDialog",
    "bulkOrderProduct",
    "bulkOrderCheckbox",
    "bulkOrderQuantity", // Added to reveal per-product quantity controllers in the modal
  ];
  static values = {
    // [{ title, price_id, price_cents, discount_title, image }, ...]
    products: Array,
    // [{ stripe_price_id, stripe_product_id, price_cents }, ...]
    prices: Array,
  };

  connect() {
    // Read Stimulus values
    const productsRaw = Array.isArray(this.productsValue) ? this.productsValue : [];
    const pricesRaw = Array.isArray(this.pricesValue) ? this.pricesValue : [];

    // Normalize and persist using the same helpers for both datasets
    const products = normalizeForStorage(productsRaw, ["stripe_product_id", "name", "discount_title", "image"]);
    persistToLocalStorage("products", products);

    const prices = normalizeForStorage(pricesRaw, ["stripe_price_id", "stripe_product_id", "price_cents", "description"]);
    persistToLocalStorage("prices", prices);

    // Build quick lookup by Stripe price id for later use
    this.pricesById = new Map(
      prices.map((pr) => [
        pr.stripe_price_id,
        {
          stripe_price_id: pr.stripe_price_id,
          stripe_product_id: pr.stripe_product_id,
          price_cents: Number(pr.price_cents || 0),
        },
      ])
    );

    // Ensure default selection state in the DOM:
    // - Each product's first price option should be selected
    // - Quantity input should be visible only for selected option
    // - The Add button inside the same product block should carry selected price id
    this._initializeSelections();
  }

  // data-action="click->store#addToCart" + data-store-price-id-param="price_abc"
  addToCart(event) {
    // event.preventDefault();
    const priceId = event.params.priceId || event.currentTarget.dataset.priceId;
    if (!priceId) return;

    const qtyInput = this._qtyForPrice(priceId);
    const qty = this._readQuantity(qtyInput);
    if (!qty || qty < 1) return;

    // Upsert cart by price id
    const cart = safeParse(localStorage.getItem("cart")) || [];
    const existing = cart.find((i) => i.price === priceId);
    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + Number(qty);
    } else {
      cart.push({ price: priceId, quantity: Number(qty) });
    }
    localStorage.setItem("cart", JSON.stringify(cart));

    // Update cart_etag using shared, simple deterministic fingerprint
    const etag = computeCartETag(cart);
    if (etag) persistString("cart_etag", etag);
    // (Future) show toast/alert here
    this._showCartOffcanvas();
  }

  // ---------- helpers ----------
  selectPrice(event) {
    const option = event.currentTarget;
    const priceId = option?.dataset?.priceId;
    if (!priceId) return;

    // Scope to the nearest product content block
    const container = option.closest(".product-content");
    if (!container) return;

    // Toggle selected class across siblings in this container
    container.querySelectorAll(".price-option").forEach((el) => {
      if (el === option) {
        el.classList.add("selected");
      } else {
        el.classList.remove("selected");
      }
    });

    // Update the Add to cart button's current price id
    const addBtn = container.querySelector('[data-store-target="addButton"]');
    if (addBtn) addBtn.dataset.priceId = priceId;
  }

  openBulkOrderModal(event) {
    event.preventDefault();

    // remember which element opened the modal so we can restore focus later
    this.lastTrigger = event.currentTarget;
    if (!this.lastTrigger) return;

    // Extract product info from the trigger's data attributes
    const { productId, productName } = this.lastTrigger.dataset;

    // ensure we have a Modal instance for the target
    if (!this.bulkOrderModal && this.hasBulkOrderModalTarget) {
      this.bulkOrderModal = Modal.getOrCreateInstance(this.bulkOrderModalTarget);
    }

    // align the modal's checkbox selection with the triggering product
    this._syncBulkOrderCheckbox(productId);

    // show the modal
    this.bulkOrderModal?.show();
  }

  // Toggle per-product quantity controls whenever a bulk order checkbox changes.
  onBulkOrderCheckboxChange(event) {
    const checkbox = event.currentTarget;
    const productId = checkbox?.value;
    if (!productId) return;

    const quantityWrapper = this._bulkOrderQuantityWrapper(productId);
    if (!quantityWrapper) return;

    const input = quantityWrapper.querySelector("input");
    const isActive = checkbox.checked;

    quantityWrapper.classList.toggle("is-active", isActive);
    if (!input) return;

    const minValue = this._quantityMinimum(input);
    input.disabled = !isActive;

    if (isActive) {
      const current = Number(input.value);
      if (!input.value || Number.isNaN(current) || current < minValue) {
        input.value = String(minValue);
      }
    } else {
      input.value = String(minValue);
    }

    this._syncBulkOrderCheckboxRequirement();
  }

  // Fired BEFORE the modal is fully hidden
  // Ensure no element inside the modal retains focus when aria-hidden is applied.
  onBulkOrderModalHide(event) {
    if (!this.hasBulkOrderModalTarget) return;
    const active = document.activeElement;
    if (active && this.bulkOrderModalTarget.contains(active)) {
      // Remove focus from elements inside the modal to prevent the warning
      active.blur();
    }
  }

  // Fired AFTER the modal is fully hidden
  // Restore focus to the element that opened the modal (good a11y practice)
  onBulkOrderModalHidden(event) {
    if (this.lastTrigger && document.body.contains(this.lastTrigger)) {
      this.lastTrigger.focus();
    }
  }

  // Launch the bulk order modal and pre-select the trigger's product when possible.
  // openBulkOrder(event) {
  //   event.preventDefault();
  //   const trigger = event.currentTarget;
  //   if (!trigger) return;

  //   const productId = trigger.dataset.productId;
  //   // this._selectBulkOrderProduct(productId);
  //   // this._toggleBulkOrder(true);
  // }

  _qtyForPrice(priceId) {
    // Find the qty input target that matches this price id
    return this.qtyTargets.find((el) => el.dataset.priceId === priceId);
  }

  _readQuantity(inputEl) {
    if (!inputEl) return 1;
    const v = Number(inputEl.value);
    return Number.isFinite(v) ? v : 1;
  }

  _initializeSelections() {
    // For each product block, ensure exactly one selected option and sync add button
    const productBlocks = document.querySelectorAll(".product-content");
    productBlocks.forEach((block) => {
      const options = Array.from(block.querySelectorAll(".price-option"));
      if (options.length === 0) return;
      // Find pre-selected (from server) or fallback to first
      let selected = options.find((el) => el.classList.contains("selected")) || options[0];
      options.forEach((el) => el.classList.toggle("selected", el === selected));

      // Set button price id accordingly
      const addBtn = block.querySelector('[data-store-target="addButton"]');
      if (addBtn) {
        const pid = selected?.dataset?.priceId;
        if (pid) addBtn.dataset.priceId = pid;
      }
    });
  }

  // _selectBulkOrderProduct(productId) {
  //   if (!this.hasBulkOrderProductTarget) return;
  //   const selectEl = this.bulkOrderProductTarget;
  //   if (!selectEl) return;

  //   if (productId) {
  //     const options = Array.from(selectEl.options || []);
  //     const matching = options.find((opt) => opt.value === String(productId));
  //     if (matching) {
  //       selectEl.value = matching.value;
  //     } else {
  //       selectEl.selectedIndex = 0;
  //     }
  //   } else {
  //     selectEl.selectedIndex = 0;
  //   }

  //   const focusField = () => {
  //     if (this.hasBulkOrderProductTarget) {
  //       this.bulkOrderProductTarget.focus();
  //     }
  //   };

  //   if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
  //     window.requestAnimationFrame(focusField);
  //   } else {
  //     setTimeout(focusField, 0);
  //   }
  // }

  // _toggleBulkOrder(isVisible) {
  //   if (!this.hasBulkOrderModalTarget) return;
  //   const modal = this.bulkOrderModalTarget;

  //   modal.classList.toggle("is-visible", isVisible);
  //   modal.setAttribute("aria-hidden", String(!isVisible));

  //   if (isVisible) {
  //     document.body.classList.add("bulk-order-lock");
  //   } else {
  //     document.body.classList.remove("bulk-order-lock");
  //   }
  // }

  _isBulkOrderVisible() {
    return this.hasBulkOrderModalTarget && this.bulkOrderModalTarget.classList.contains("is-visible");
  }

  _syncBulkOrderCheckbox(productId) {
    // Bail if there is no product to sync; the modal keeps the prior selection.
    if (!productId || !this.hasBulkOrderCheckboxTarget) return;

    const targetValue = String(productId);

    // Walk through every checkbox and toggle only the one matching the trigger's value.
    this.bulkOrderCheckboxTargets.forEach((checkbox) => {
      if (checkbox.value !== targetValue) return;

      if (!checkbox.checked) {
        checkbox.checked = true;
      }

      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  // Note: JSON helpers and etag computation moved to shared utils

  _showCartOffcanvas() {
    try {
      const el = document.getElementById("cartOffcanvas");
      if (!el) return;
      const oc = Offcanvas.getOrCreateInstance(el);
      oc.show();
    } catch (_) {
      // ignore if Bootstrap JS is unavailable
    }
  }

  _bulkOrderQuantityWrapper(productId) {
    // Locate the matching quantity container tied to a checkbox value.
    return this.bulkOrderQuantityTargets.find((el) => el.dataset.productId === String(productId));
  }

  _quantityMinimum(input) {
    if (!input) return 1;
    const minAttr = Number(input.min);
    return Number.isFinite(minAttr) && minAttr > 0 ? minAttr : 1;
  }

  _syncBulkOrderCheckboxRequirement() {
    if (!this.hasBulkOrderCheckboxTarget) return;

    const anyChecked = this.bulkOrderCheckboxTargets.some((checkbox) => checkbox.checked);
    this.bulkOrderCheckboxTargets.forEach((checkbox, index) => {
      checkbox.required = !anyChecked && index === 0;
    });
  }
}
